/**
 * UI-only helpers for land donation requests. Maps API stage strings to stable
 * categories using the same `includes` priority as the legacy `stageLabel` / `stageBadge`.
 */

export type LandRequestStageCategory =
  | "draft"
  | "inProgress"
  | "submitted"
  | "approved"
  | "rejected"
  | "unknown";

const combined = (stage?: string | null, state?: string | null) =>
  `${String(stage || "")} ${String(state || "")}`.toLowerCase().trim();

/**
 * Same classification order as the original stageLabel in DonationRequestBase.
 */
export function normalizeLandRequestStage(
  stage?: string | null,
  state?: string | null
): LandRequestStageCategory {
  const s = combined(stage, state);
  if (!s) return "unknown";
  if (s.includes("draft")) return "draft";
  if (s.includes("progress")) return "inProgress";
  if (s.includes("submitted")) return "submitted";
  if (s.includes("approved") || s.includes("done")) return "approved";
  if (s.includes("reject")) return "rejected";
  return "unknown";
}

/** Arabic label for table/summary (matches legacy stageLabel). */
export function labelForLandRequestStage(stage?: string | null, state?: string | null): string {
  const raw = String(stage || state || "").trim();
  const s = raw.toLowerCase();
  if (!s) return "-";
  // Show cancellation statuses in Arabic before checking other categories.
  if (["cancel", "cancelled", "canceled"].includes(s)) return "مرفوض";

  const cat = normalizeLandRequestStage(stage, state);
  switch (cat) {
    case "draft":
      return "مسودة";
    case "inProgress":
      return "قيد المعالجة";
    case "submitted":
      return "تم الإرسال";
    case "approved":
      return "معتمد";
    case "rejected":
      return "مرفوض";
    default:
      return raw || "غير معروف";
  }
}

/** Tailwind classes for badge (ring + bg) — matches UnifiedMyRequestsPage stageBadge. */
export function badgeClassForLandRequestStage(stage?: string | null, state?: string | null): string {
  const cat = normalizeLandRequestStage(stage, state);
  switch (cat) {
    case "draft":
      return "bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200";
    case "inProgress":
      return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
    case "submitted":
      return "bg-amber-50 text-amber-800 ring-1 ring-amber-200";
    case "approved":
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    case "rejected":
      return "bg-red-50 text-red-700 ring-1 ring-red-200";
    default:
      return "bg-neutral-50 text-neutral-800 ring-1 ring-neutral-200";
  }
}

export type JourneySectionId = "land" | "applicant" | "requests" | "send";

export type PreSubmitStepDef = {
  key: string;
  /** Section to open when this step is clicked */
  section: JourneySectionId;
  label: string;
};

export const PRE_SUBMIT_STEP_DEFS: PreSubmitStepDef[] = [
  { key: "land", section: "land", label: "بيانات الأرض" },
  { key: "applicant", section: "applicant", label: "بيانات مقدم الطلب" },
  { key: "documents", section: "requests", label: "المستندات والمرفقات" },
  { key: "send", section: "send", label: "إرسال الطلب" },
];

/**
 * Pure UI state for the horizontal stepper (pre-submit flow + submitted state).
 */
export function getPreSubmitStepperState(params: {
  landLoaded: boolean;
  activeSection: JourneySectionId;
  hasRequestRow: boolean;
  isDraft: boolean;
  requirementsMet: boolean;
}): {
  completed: boolean[];
  activeIndex: number;
} {
  const { landLoaded, activeSection, hasRequestRow, isDraft, requirementsMet } = params;

  const completed: boolean[] = [
    landLoaded,
    landLoaded && hasRequestRow,
    landLoaded && hasRequestRow && (!isDraft || requirementsMet),
    landLoaded && hasRequestRow && !isDraft,
  ];

  let activeIndex = 0;
  if (!landLoaded) {
    activeIndex = 0;
  } else if (activeSection === "land") {
    activeIndex = 0;
  } else if (activeSection === "applicant") {
    activeIndex = 1;
  } else if (activeSection === "requests") {
    activeIndex = 2;
  } else if (activeSection === "send") {
    activeIndex = 3;
  }

  return { completed, activeIndex };
}

/**
 * Post-submit timeline (UI only). Indices match POST_SUBMIT_TIMELINE_STEPS.
 * "completed" steps before current use filled styling; future steps stay muted.
 */
export function postSubmitTimelineState(category: LandRequestStageCategory): {
  currentIndex: number;
} {
  switch (category) {
    case "submitted":
      return { currentIndex: 0 };
    case "inProgress":
      return { currentIndex: 2 };
    case "approved":
    case "rejected":
      return { currentIndex: 3 };
    case "draft":
    case "unknown":
    default:
      return { currentIndex: 1 };
  }
}

export const POST_SUBMIT_TIMELINE_STEPS = [
  { key: "sent", label: "تم الإرسال" },
  { key: "received", label: "استلام الطلب" },
  { key: "review", label: "قيد المراجعة" },
  { key: "decision", label: "القرار النهائي" },
] as const;
